'use client'

import UnifiedPlayMark from '@/components/embedding/UnifiedPlayMark'
import { FC } from 'react'

export interface SPVideoControlsProps {
  playbackRate: number
  isMuted: boolean
  showControls: boolean
  hasPrevious: boolean
  hasNext: boolean
  isDetailMode: boolean
  onFullClose: () => void
  onPlaybackRateChange: () => void
  onMuteToggle: () => void
  onPrevious: () => void
  onPlay: () => void
  onNext: () => void
}

const SPVideoControls: FC<SPVideoControlsProps> = ({
  playbackRate,
  isMuted,
  showControls,
  hasPrevious,
  hasNext,
  isDetailMode,
  onFullClose,
  onPlaybackRateChange,
  onMuteToggle,
  onPrevious,
  onPlay,
  onNext,
}) => {
  return (
    <>
      <style>{`
        /* A4: Always-visible controls container */
        .sp-right-controls {
          position: absolute;
          top: calc(12px + env(safe-area-inset-top, 0px));
          right: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          z-index: 20;
        }

        .sp-close-button {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.5);
          border: 1px solid rgba(255, 255, 255, .3);
          border-radius: 50%;
          cursor: pointer;
          color: white;
        }

        .sp-speed-button {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.5);
          border: 1px solid rgba(255, 255, 255, .3);
          border-radius: 50%;
          cursor: pointer;
          color: white;
          font-size: 12.5px;
          font-weight: 600;
        }

        .sp-mute-button {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.5);
          border: 1px solid rgba(255, 255, 255, .3);
          border-radius: 50%;
          cursor: pointer;
          color: white;
        }

        .sp-play-controls {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 40px;
          z-index: 15;
          animation: spControlsFadeIn 500ms ease-out;
        }

        @keyframes spControlsFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .sp-nav-button {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 50%;
          cursor: pointer;
          color: white;
          transition: all 200ms ease;
        }

        .sp-nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .sp-play-button {
          width: 72px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          color: white;
          padding: 0;
        }
      `}</style>

      {/* A4: Always-visible controls: Close (X), Speed, Mute - always shown except in detail mode */}
      {!isDetailMode && (
        <div className="sp-right-controls">
          {/* A3: X button = full close (stop video + hide modal) */}
          <button type="button" className="sp-close-button" onClick={onFullClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <button type="button" className="sp-speed-button" onClick={onPlaybackRateChange}>
            {playbackRate}x
          </button>
          <button type="button" className="sp-mute-button" onClick={onMuteToggle}>
            {isMuted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Play controls - center of video (not in detail mode) - keep show/hide logic */}
      {showControls && !isDetailMode && (
        <div className="sp-play-controls">
          <button
            type="button"
            className="sp-nav-button"
            onClick={onPrevious}
            disabled={!hasPrevious}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <button type="button" aria-label="再生" className="sp-play-button group" onClick={onPlay}>
            <UnifiedPlayMark />
          </button>

          <button type="button" className="sp-nav-button" onClick={onNext} disabled={!hasNext}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </>
  )
}

export default SPVideoControls
